// Shopify Storefront GraphQL helper — client-side, uses public Storefront token.
// The Storefront token is safe to expose (unauthenticated_read_product_listings).

const DOMAIN = (process.env.REACT_APP_SHOPIFY_DOMAIN || "").trim().replace(/^https?:\/\//, "").replace(/\/+$/, "");
const TOKEN = (process.env.REACT_APP_SHOPIFY_STOREFRONT_TOKEN || "").trim();
const VERSION = (process.env.REACT_APP_SHOPIFY_API_VERSION || "2024-10").trim();

export const SHOPIFY_CONFIGURED = Boolean(DOMAIN && TOKEN);

async function gql(query, variables = {}) {
    if (!SHOPIFY_CONFIGURED) throw new Error("Shopify not configured");
    const res = await fetch(`https://${DOMAIN}/api/${VERSION}/graphql.json`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token": TOKEN,
            "Accept": "application/json",
        },
        body: JSON.stringify({ query, variables }),
    });
    if (!res.ok) throw new Error(`Shopify request failed: ${res.status}`);
    const json = await res.json();
    if (json.errors) throw new Error(json.errors.map((e) => e.message).join("; "));
    return json.data;
}

const PRODUCTS_QUERY = `
query Products($first: Int!) {
  products(first: $first, sortKey: BEST_SELLING) {
    edges {
      node {
        id
        title
        handle
        description
        onlineStoreUrl
        priceRange { minVariantPrice { amount currencyCode } }
        images(first: 1) { edges { node { url altText } } }
        variants(first: 1) { edges { node { id availableForSale } } }
      }
    }
  }
}`;

export async function fetchProducts(first = 24) {
    const data = await gql(PRODUCTS_QUERY, { first });
    return (data.products.edges || []).map(({ node }) => ({
        id: node.id,
        title: node.title,
        handle: node.handle,
        description: node.description,
        onlineStoreUrl: node.onlineStoreUrl,
        price: node.priceRange?.minVariantPrice
            ? formatMoney(node.priceRange.minVariantPrice.amount, node.priceRange.minVariantPrice.currencyCode)
            : "",
        image: node.images?.edges?.[0]?.node?.url || "",
        imageAlt: node.images?.edges?.[0]?.node?.altText || node.title,
        variantId: node.variants?.edges?.[0]?.node?.id || null,
        available: node.variants?.edges?.[0]?.node?.availableForSale ?? true,
    }));
}

const CART_CREATE = `
mutation CartCreate($lines: [CartLineInput!]!) {
  cartCreate(input: { lines: $lines }) {
    cart { id checkoutUrl }
    userErrors { field message }
  }
}`;

export async function createCartCheckoutUrl(variantId, quantity = 1) {
    const data = await gql(CART_CREATE, { lines: [{ merchandiseId: variantId, quantity }] });
    const errs = data.cartCreate?.userErrors || [];
    if (errs.length) throw new Error(errs.map((e) => e.message).join("; "));
    return data.cartCreate.cart.checkoutUrl;
}

function formatMoney(amount, currency) {
    try {
        return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(parseFloat(amount));
    } catch (_e) {
        return `${amount} ${currency}`;
    }
}

export const SHOPIFY_DOMAIN = DOMAIN;
